import { captureException } from "@/monitoring";
import { engagementEventSchema } from "@/schemas";

import { impressionConfig } from "./config";
import { newEventId } from "./utils";
import { stampEnvelope } from "./session";
import { enqueue } from "./queue";

import type { EngagementAction, EngagementEvent } from "@/schemas";
import type { SurfaceKey } from "@/contexts/surface-context";
import type { TrackTarget } from "./types";

interface EngagementInput {
  action: EngagementAction;
  target: TrackTarget;
  surface: SurfaceKey;
  /** Absolute feed depth, already resolved by the caller; null when off-feed or unresolved. */
  position: number | null;
  /** follow only */
  targetAccountId?: string;
}

/**
 * Emit one engagement event for a committed user action, riding the same buffer/batch/retry/TTL
 * transport as impressions (TMTG-16/19). Call only from a true server-success handler — an
 * optimistic update that rolls back must emit nothing (AC1/AC5). `event_time` is the emission
 * moment (the action just committed), not backdated like an impression's became-visible time.
 */
const captureEngagement = (input: EngagementInput): void => {
  const { sessionId, clientSeq } = stampEnvelope();

  const event: EngagementEvent = {
    event_id: newEventId(),
    event_type: "engagement",
    action: input.action,
    event_time: new Date().toISOString(),
    status_id: input.target.statusId,
    session_id: sessionId,
    client_seq: clientSeq,
    surface: input.surface,
    device: "web",
    app_version: impressionConfig.appVersion,
  };

  if (input.target.authorAccountId) {
    event.author_account_id = input.target.authorAccountId;
  }
  if (input.targetAccountId) event.target_account_id = input.targetAccountId;
  if (input.position !== null) event.position = input.position;
  if (input.target.signature) event.signature = input.target.signature;

  const parsed = engagementEventSchema.safeParse(event);
  if (!parsed.success) {
    captureException(parsed.error);
    return;
  }
  enqueue(parsed.data);
};

export { captureEngagement, type EngagementInput };
